import { useCallback, useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ProfileNavigation from '../../../components/common/ProfileNavigation';
import SEO from '../../../components/SEO';
import useUser, { isUserDoctor } from '../../../hooks/User';
import usePatientChat from '../../../hooks/usePatientChat';
import { ErrorHandlerContextProvider } from '../../../contexts/ErrorHandlerContext';
import ErrorHandler from '../../../components/common/ErrorHandler';
import { ROUTES } from '../../../routes';
import dayjs from 'dayjs';
import './patientchat.css';

// ponytail: inline markdown renderer, no dependency
function renderMD(text) {
  const escape = (s) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  let html = escape(text);
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  html = html.replace(/---+/g, '<hr>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/\n/g, '<br>');
  // wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*?<br>?)+)/g, '<ul>$1</ul>');
  return html;
}

// ─── Inline Icons ───────────────────────────────────────────────

const ArrowLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 5v14"/><path d="M5 12h14"/>
    </svg>
);

const SendIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m5 12 7-7 7 7"/><path d="M12 19V5"/>
    </svg>
);

const ChatIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
);

const MessageSquareIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
);

// ─── Session sidebar item ───────────────────────────────────────

function SessionItem({ session, isActive, onClick }) {
    const title = session.title || 'Анализ данных';
    const date = session.creation_dt
        ? dayjs(session.creation_dt).format('DD.MM.YYYY')
        : '';

    return (
        <button
            className={`chat-session-item ${isActive ? 'active' : ''}`}
            onClick={onClick}
        >
            <div className="chat-session-item__icon">
                <MessageSquareIcon />
            </div>
            <div className="chat-session-item__body">
                <span className="chat-session-item__title">{title}</span>
                {date && (
                    <span className="chat-session-item__date">{date}</span>
                )}
            </div>
        </button>
    );
}

// ─── Single message bubble ───────────────────────────────────────

function MessageBubble({ message }) {
    const role = message.role || 'assistant';
    const content = message.content || '';
    const timestamp = message.creation_dt
        ? dayjs(message.creation_dt).format('HH:mm')
        : '';

    const roleLabel = {
        user: 'Вы',
        assistant: 'AI Анализ',
        system: 'Система',
    }[role] || role;

    return (
        <div className={`chat-message chat-message--${role}`}>
            <div className="chat-message__header">
                <span className="chat-message__role">{roleLabel}</span>
                {timestamp && (
                    <span className="chat-message__time">{timestamp}</span>
                )}
            </div>
            <div className="chat-message__content"
                 dangerouslySetInnerHTML={{ __html: renderMD(content) }}>
            </div>
        </div>
    );
}

// ─── Main PatientChat component ──────────────────────────────────

function PatientChat() {
    const { loading: userLoading, error: userError, data: userData } = useUser();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const patientId = searchParams.get('patientId');

    const {
        sessions,
        messages,
        currentSessionId,
        sessionsLoading,
        messagesLoading,
        sendingMessage,
        sessionsError,
        messagesError,
        sendMessageError,
        fetchSessions,
        createSession,
        fetchMessages,
        sendMessage,
    } = usePatientChat();

    const [inputText, setInputText] = useState('');

    // Auth guard
    useEffect(() => {
        if (userLoading) return;
        if (userError) {
            navigate(ROUTES.AUTH, { viewTransition: true });
            return;
        }
        if (!isUserDoctor(userData)) {
            navigate(ROUTES.PROFILE, { viewTransition: true });
        }
    }, [userLoading, userError, userData, navigate]);

    // Load sessions when patientId is available
    useEffect(() => {
        if (!patientId || userLoading) return;
        fetchSessions(patientId);
    }, [patientId, userLoading, fetchSessions]);

    // Scroll messages to bottom on new messages
    useEffect(() => {
        const container = document.querySelector('.chat-messages');
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages, messagesLoading]);

    const handleSessionClick = useCallback((session) => {
        if (!patientId) return;
        fetchMessages(patientId, session.id);
    }, [patientId, fetchMessages]);

    const handleNewSession = useCallback(async () => {
        if (!patientId) return;
        const newSession = await createSession(patientId);
        if (newSession) {
            fetchMessages(patientId, newSession.id);
        }
    }, [patientId, createSession, fetchMessages]);

    const handleSend = useCallback(async () => {
        const text = inputText.trim();
        if (!text || !patientId || !currentSessionId || sendingMessage) return;

        setInputText('');
        await sendMessage(patientId, currentSessionId, text);
    }, [inputText, patientId, currentSessionId, sendingMessage, sendMessage]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    // Loading guard
    if (userLoading) {
        return (
            <div className="patient-chat-page">
                <ProfileNavigation isDoctor={true} />
                <div className="patient-chat-loading">
                    <div className="spinner" />
                    <p>Загрузка...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="patient-chat-page">
            <ProfileNavigation isDoctor={isUserDoctor(userData)} />

            <div className="patient-chat-container">
                {/* Header */}
                <div className="patient-chat-header">
                    <button
                        className="patient-chat-back"
                        onClick={() => navigate(ROUTES.PATIENTS, { viewTransition: true })}
                    >
                        <ArrowLeftIcon />
                        <span>Назад к пациенту</span>
                    </button>
                    <button
                        className="patient-chat-new"
                        onClick={handleNewSession}
                        disabled={sessionsLoading}
                    >
                        <PlusIcon />
                        <span>Новый анализ</span>
                    </button>
                </div>

                {/* Body */}
                <div className="patient-chat-body">
                    {/* Sessions sidebar */}
                    <div className="patient-chat-sessions">
                        <div className="patient-chat-sessions__header">
                            <ChatIcon />
                            <span>Сессии чата</span>
                        </div>

                        {sessionsLoading ? (
                            <div className="patient-chat-sessions__loading">
                                <div className="spinner spinner--sm" />
                                <span>Загрузка...</span>
                            </div>
                        ) : sessionsError ? (
                            <div className="patient-chat-sessions__error">
                                {sessionsError}
                            </div>
                        ) : sessions.length === 0 ? (
                            <div className="patient-chat-sessions__empty">
                                Нет сессий чата.<br />Нажмите «Новый анализ».
                            </div>
                        ) : (
                            <div className="patient-chat-sessions__list">
                                {sessions.map((session) => (
                                    <SessionItem
                                        key={session.id}
                                        session={session}
                                        isActive={session.id === currentSessionId}
                                        onClick={() => handleSessionClick(session)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Messages panel */}
                    <div className="patient-chat-messages-panel">
                        {!currentSessionId ? (
                            <div className="patient-chat-empty">
                                <ChatIcon />
                                <p>Выберите сессию или создайте новый анализ</p>
                            </div>
                        ) : (
                            <>
                                {/* Messages */}
                                <div className="chat-messages">
                                    {messagesLoading ? (
                                        <div className="chat-messages__loading">
                                            <div className="spinner" />
                                            <p>Загрузка сообщений...</p>
                                        </div>
                                    ) : messagesError ? (
                                        <div className="chat-messages__error">
                                            {messagesError}
                                        </div>
                                    ) : messages.length === 0 ? (
                                        <div className="chat-messages__empty">
                                            Сообщений пока нет
                                        </div>
                                    ) : (
                                        messages.map((msg, idx) => (
                                            <MessageBubble key={msg.id || idx} message={msg} />
                                        ))
                                    )}
                                    {sendingMessage && (
                                        <div className="chat-message chat-message--assistant chat-message--loading">
                                            <div className="chat-message__header">
                                                <span className="chat-message__role">AI Анализ</span>
                                            </div>
                                            <div className="chat-message__content">
                                                <span className="typing-indicator">
                                                    <span /><span /><span />
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Input area */}
                                <div className="chat-input-area">
                                    {sendMessageError && (
                                        <div className="chat-input-area__error">
                                            {sendMessageError}
                                        </div>
                                    )}
                                    <div className="chat-input-area__row">
                                        <textarea
                                            className="chat-input-area__textarea"
                                            value={inputText}
                                            onChange={(e) => setInputText(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            placeholder="Введите сообщение..."
                                            rows={1}
                                            disabled={sendingMessage}
                                        />
                                        <button
                                            className="chat-input-area__send"
                                            onClick={handleSend}
                                            disabled={!inputText.trim() || sendingMessage}
                                            title="Отправить"
                                        >
                                            <SendIcon />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Export wrapped with error handler
export default function PatientChatPage() {
    return (
        <>
            <SEO
                title="AI Анализ пациента"
                description="RAG-анализ данных пациента с помощью ИИ"
                noindex={true}
            />
            <ErrorHandlerContextProvider>
                <PatientChat />
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}
